/**
 * SidebarAd Component
 * Reusable sidebar advertisement component optimized for vertical placement
 * with sticky positioning and responsive behavior
 */

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useAdTracking } from "@/hooks/useAdTracking";
import type { Advertisement } from "@/types/advertising";

interface SidebarAdProps {
  ad: Advertisement;
  className?: string;
  sticky?: boolean;
  stickyOffset?: number;
}

/**
 * SidebarAd component displays vertical sidebar advertisements
 * with optional sticky behavior and automatic tracking
 */
const SidebarAd: React.FC<SidebarAdProps> = ({
  ad,
  className = "",
  sticky = false,
  stickyOffset = 80,
}) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { adRef, handleClick, impressionTracked } = useAdTracking({
    adId: ad.id,
    placement: ad.placement,
    targetUrl: ad.targetUrl,
  });

  const handleImageError = () => {
    console.error(`Failed to load sidebar ad image: ${ad.imageUrl}`);
    setImageError(true);
    setIsLoading(false);
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleAdClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    handleClick(event);
  };

  // Don't render if ad is not active
  if (!ad.isActive) {
    return null;
  }

  const stickyClasses = sticky ? `sticky top-[${stickyOffset}px]` : "";

  const renderAdContent = () => {
    switch (ad.format) {
      case "image":
        return renderImageAd();
      case "text":
        return renderTextAd();
      case "video":
        return renderVideoAd();
      default:
        return renderImageAd();
    }
  };

  const renderImageAd = () => {
    if (!ad.imageUrl || imageError) {
      return renderTextAd();
    }

    return (
      <div className="relative w-full aspect-[300/600] md:aspect-[300/250]">
        {isLoading && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-lg" />
        )}
        <Image
          src={ad.imageUrl}
          alt={ad.altText || ad.title}
          fill
          className="object-cover rounded-lg"
          onError={handleImageError}
          onLoad={handleImageLoad}
          sizes="(max-width: 768px) 100vw, 300px"
        />
      </div>
    );
  };

  const renderTextAd = () => {
    return (
      <div className="flex flex-col justify-center items-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg min-h-[250px]">
        <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">
          {ad.title}
        </h3>
        {ad.description && (
          <p className="text-sm text-gray-700 text-center mb-4">
            {ad.description}
          </p>
        )}
        <div className="mt-auto px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-semibold hover:bg-purple-700 transition-colors">
          Learn More
        </div>
      </div>
    );
  };

  const renderVideoAd = () => {
    if (!ad.videoUrl) {
      return renderTextAd();
    }

    return (
      <div className="relative w-full aspect-[300/600] md:aspect-[300/250]">
        <video
          src={ad.videoUrl}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover rounded-lg"
          onError={() => {
            console.error(`Failed to load sidebar ad video: ${ad.videoUrl}`);
            setImageError(true);
          }}
        >
          Your browser does not support the video tag.
        </video>
      </div>
    );
  };

  return (
    <div
      ref={adRef}
      className={`sidebar-ad ${stickyClasses} ${className}`}
      data-ad-id={ad.id}
      data-tracked={impressionTracked}
      role="complementary"
      aria-label="Sidebar Advertisement"
    >
      <div className="relative">
        {/* Advertisement label */}
        <div className="absolute top-2 right-2 z-10">
          <span className="text-xs bg-gray-800 bg-opacity-75 text-white px-2 py-1 rounded">
            Ad
          </span>
        </div>

        <Link
          href={ad.targetUrl}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={handleAdClick}
          className="block w-full transition-transform hover:scale-[1.02] duration-200"
          aria-label={`Advertisement: ${ad.title}`}
        >
          {renderAdContent()}
        </Link>
      </div>

      {/* Additional info or CTA for sidebar */}
      {ad.description && ad.format === "image" && !imageError && (
        <div className="mt-2 p-2 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-600 line-clamp-2">{ad.description}</p>
        </div>
      )}
    </div>
  );
};

export default SidebarAd;
